import type { CSSProperties } from 'react';

import type { INodeConfigProps } from './index';

const operators = ['==', '!=', '<', '<=', '>', '>=', 'in', 'contains'] as const;
type Operator = (typeof operators)[number];

interface IThresholdRule {
  field: string;
  op: Operator;
  value: unknown;
  branch: string;
}

const ruleCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const ruleHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const ruleGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: 'var(--sp-2)',
  alignItems: 'end',
};

/**
 * `threshold-check` nodes route the run to a named branch based on the
 * first matching rule against the node input. Mirrors the runtime
 * schema in `packages/node-types/src/threshold-check/index.ts`:
 *   - `rules` (required, at least 1) — `{ field, op, value, branch }`
 *   - `default` (required) — branch label when no rule matches
 *
 * `value` is `unknown` server-side. The form holds it as a text input
 * and round-trips through `serializeRuleValue` / `parseRuleValue` —
 * numeric and boolean literals get typed, everything else stays a
 * string. (Power users wanting object / array values fall back to the
 * raw JSON editor by changing the node's type tag temporarily.)
 */
export function ThresholdCheckConfig({ config, onChange }: INodeConfigProps) {
  const rules = Array.isArray(config.rules) ? (config.rules as IThresholdRule[]) : [];
  const defaultBranch = typeof config.default === 'string' ? config.default : 'UNKNOWN';

  function setRules(next: IThresholdRule[]) {
    onChange({ rules: next });
  }

  function updateRule(index: number, patch: Partial<IThresholdRule>) {
    const next = rules.slice();
    const current = next[index];
    if (!current) return;
    next[index] = { ...current, ...patch };
    setRules(next);
  }

  function addRule() {
    setRules([...rules, { field: '', op: '==', value: '', branch: '' }]);
  }

  function removeRule(index: number) {
    setRules(rules.filter((_, i) => i !== index));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div>
        <div
          className="t-mono-label"
          style={{
            marginBottom: 'var(--sp-1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Rules · evaluated top-down</span>
          <button
            type="button"
            className="btn btn--tertiary btn--xs"
            onClick={addRule}
            aria-label="Add a threshold rule"
          >
            + Add rule
          </button>
        </div>
        {rules.length === 0 ? (
          <p className="t-secondary t-xs" style={{ margin: 0 }}>
            No rules yet — at least one is required. The runtime falls through to the default branch
            only when every rule above failed to match.
          </p>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {rules.map((rule, i) => (
            <article key={i} style={ruleCard}>
              <div style={ruleHeader}>
                <span className="t-mono-label">Rule {i + 1}</span>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => removeRule(i)}
                  aria-label={`Remove rule ${i + 1}`}
                >
                  Remove
                </button>
              </div>
              <div style={ruleGrid}>
                <label className="field">
                  <span className="field-label">Field</span>
                  <input
                    className="input"
                    value={rule.field}
                    onChange={(e) => updateRule(i, { field: e.target.value })}
                    placeholder="e.g. severity"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Op</span>
                  <select
                    className="select-native"
                    value={rule.op}
                    onChange={(e) => updateRule(i, { op: e.target.value as Operator })}
                  >
                    {operators.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Value</span>
                  <input
                    className="input"
                    value={serializeRuleValue(rule.value)}
                    onChange={(e) => updateRule(i, { value: parseRuleValue(e.target.value) })}
                    placeholder='e.g. 8 or "BATTERY_LOW"'
                  />
                </label>
              </div>
              <label className="field">
                <span className="field-label">Branch label (output when this rule matches)</span>
                <input
                  className="input"
                  value={rule.branch}
                  onChange={(e) => updateRule(i, { branch: e.target.value })}
                  placeholder="e.g. HIGH"
                />
              </label>
            </article>
          ))}
        </div>
      </div>

      <label className="field">
        <span className="field-label">Default branch</span>
        <input
          className="input"
          value={defaultBranch}
          onChange={(e) => onChange({ default: e.target.value })}
          placeholder="e.g. UNKNOWN"
        />
        <span className="field-help">
          Emitted when no rule above matches. The downstream edge for this label is what gets taken
          in the fallback case.
        </span>
      </label>
    </div>
  );
}

/**
 * Convert a rule's typed `value` to the string the text input displays.
 * Numbers and booleans render as their literal form; strings render
 * unwrapped (no surrounding quotes); arrays / objects fall back to
 * JSON. `null` / `undefined` render as the empty string so the input
 * doesn't show "null" before the user has typed anything.
 */
export function serializeRuleValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

/**
 * Round-trip the value text input back to a typed `unknown` for the
 * rule. Recognized literal forms: integers / floats become `number`,
 * `true` / `false` become `boolean`, `null` becomes `null`. Anything
 * else (and any string with surrounding whitespace stripped) stays a
 * string so the common case ("BATTERY_LOW", "ok", etc.) Just Works.
 *
 * Exported for unit-testing — the conversion has more edge cases than
 * the rest of this file combined.
 */
export function parseRuleValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  // Number literal: `1`, `-2`, `3.14`. Avoid `Number('')` returning 0
  // and avoid matching strings that just happen to start with a digit
  // ("123abc"). The exact-numeric regex below is what JSON uses.
  if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return raw;
}
